import { translate, type MessageKey, type PoiCategory } from "@festipal/shared";
import { useCallback, useEffect, useState } from "react";

import { festivals, type ErrorReport, type ReportStatus } from "./api";

/**
 * Error reports sent from the app.
 *
 * Anyone can report, signed in or not, in one gesture from a set or a place
 * (CONTENU.md §4). Nothing changes by itself: someone looks, fixes the lineup
 * or the place in their own tab, then marks the report fixed, or dismisses it.
 */

const STATUS_LABELS: Record<ReportStatus, string> = {
  OPEN: "À traiter",
  RESOLVED: "Corrigés",
  DISMISSED: "Écartés",
};

/** "Les Essais — Grande scène, ven. 21:00", in the browser's words. */
function describe(report: ErrorReport): string {
  const target = report.target;
  if (report.targetType === "FESTIVAL") return "Le festival";
  if (!target) return "Élément supprimé depuis";
  if ("startsAt" in target) {
    const when = new Date(target.startsAt).toLocaleString("fr-FR", {
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${target.name} — ${[target.stage, when].filter(Boolean).join(", ")}`;
  }
  const category = translate(
    "fr",
    `poi.category.${target.category as PoiCategory}` as MessageKey,
  );
  return target.name ? `${target.name} (${category})` : category;
}

export function ReportsEditor({ slug }: { slug: string }) {
  const [status, setStatus] = useState<ReportStatus>("OPEN");
  const [items, setItems] = useState<ErrorReport[]>([]);
  const [open, setOpen] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const result = await festivals.reports(slug, status);
      setItems(result.reports);
      setOpen(result.open);
      setError(null);
    } catch {
      setError("Lecture impossible.");
    }
  }, [slug, status]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const review = async (id: string, next: ReportStatus) => {
    setBusy(id);
    try {
      await festivals.reviewReport(slug, id, next);
      await reload();
    } catch {
      setError("Décision non enregistrée.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="card">
      <h2>Signalements</h2>
      <p className="muted">
        Envoyés depuis l'application par les festivaliers, avec ou sans compte : rien de
        ce qui les identifie n'est gardé. Corrigez le programme ou le lieu dans son
        onglet, puis marquez le signalement corrigé. Ils sont effacés 90 jours après le
        festival.
      </p>

      <div className="row">
        {(["OPEN", "RESOLVED", "DISMISSED"] as const).map((value) => (
          <button
            key={value}
            onClick={() => setStatus(value)}
            className={status === value ? "tab active" : "tab"}
            aria-pressed={status === value}
          >
            {STATUS_LABELS[value]}
            {value === "OPEN" && open > 0 ? ` (${open})` : ""}
          </button>
        ))}
      </div>

      {error ? <p className="error">{error}</p> : null}

      {items.length === 0 ? (
        <p className="muted">
          {status === "OPEN" ? "Aucun signalement à traiter." : "Aucun signalement."}
        </p>
      ) : (
        // Cards rather than a table: the comment is the substance, and a table
        // squeezed it into a column one word wide on a phone.
        items.map((item) => (
          <article key={item.id} className="report">
            <h3>{describe(item)}</h3>
            <p className="muted">
              {translate("fr", `report.kind.${item.kind}` as MessageKey)} · reçu le{" "}
              {new Date(item.createdAt).toLocaleString("fr-FR", {
                dateStyle: "short",
                timeStyle: "short",
              })}
            </p>
            {item.comment ? <p>{item.comment}</p> : null}
            <div className="row">
              {item.status === "OPEN" ? (
                <>
                  <button
                    className="primary"
                    disabled={busy === item.id}
                    onClick={() => void review(item.id, "RESOLVED")}
                  >
                    Corrigé
                  </button>
                  <button
                    disabled={busy === item.id}
                    onClick={() => void review(item.id, "DISMISSED")}
                  >
                    Écarter
                  </button>
                </>
              ) : (
                <button
                  disabled={busy === item.id}
                  onClick={() => void review(item.id, "OPEN")}
                >
                  Rouvrir
                </button>
              )}
            </div>
          </article>
        ))
      )}
    </section>
  );
}
